import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ContactPage from './pages/ContactPage';
import CustomerDashboard from './pages/CustomerDashboard';
import ServiceRequestForm from './pages/ServiceRequestForm';
import AdminLogin from './pages/AdminLogin';
import UstaLogin from './pages/UstaLogin';
import UstaRegister from './pages/UstaRegister';
import NedenBizeKatil from './pages/NedenBizeKatil';
import GarantiPage from './pages/GarantiPage';
import GarantiSartlari from './pages/GarantiSartlari';
import HakkimizdaPage from './pages/HakkimizdaPage';
import GizlilikPolitikasi from './pages/GizlilikPolitikasi';
import KullanimKosullari from './pages/KullanimKosullari';
import BilgiGuvenligi from './pages/BilgiGuvenligi';
import SSS from './pages/SSS';
import AnkaraKombiServisi from './pages/AnkaraKombiServisi';
import IstanbulKombiServisi from './pages/IstanbulKombiServisi';
import IzmirKombiServisi from './pages/IzmirKombiServisi';
import KlimaServisi from './pages/KlimaServisi';
import BeyazEsyaServisi from './pages/BeyazEsyaServisi';
import ElektrikServisi from './pages/ElektrikServisi';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import SEOServicePage from './pages/SEOServicePage';
import GoogleAdsTracker from './components/GoogleAdsTracker';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import SEOHead from './components/SEOHead';
import './App.css';

// Lazy load heavy dashboard components
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UstaDashboard = lazy(() => import('./pages/UstaDashboard'));
const UstaDashboardNew = lazy(() => import('./pages/UstaDashboardNew'));

// Loading fallback component
const DashboardLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#006D77] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600">Yükleniyor...</p>
    </div>
  </div>
);

function App() {
  // Ziyaretçi takibi
  React.useEffect(() => {
    const trackVisitor = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await fetch(`${API_URL}/api/admin/track-visitor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: window.location.pathname,
            referrer: document.referrer,
            user_agent: navigator.userAgent
          })
        });
      } catch (e) {
        // Silent fail
      }
    };
    trackVisitor();
  }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <SEOHead />
      <GoogleAdsTracker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/hizmetler" element={<ServicesPage />} />
        <Route path="/nasil-calisir" element={<HowItWorksPage />} />
        <Route path="/iletisim" element={<ContactPage />} />
        <Route path="/hesabim" element={<CustomerDashboard />} />
        <Route path="/servis-talep/:category" element={<ServiceRequestForm />} />
        <Route path="/talep/:category" element={<ServiceRequestForm />} />
        <Route path="/proadmin" element={<AdminLogin />} />
        <Route path="/proadmin/dashboard" element={
          <Suspense fallback={<DashboardLoader />}>
            <AdminDashboard />
          </Suspense>
        } />
        <Route path="/prousta" element={<UstaLogin />} />
        <Route path="/prousta/register" element={<UstaRegister />} />
        <Route path="/prousta/dashboard" element={
          <Suspense fallback={<DashboardLoader />}>
            <UstaDashboardNew />
          </Suspense>
        } />
        <Route path="/neden-bize-katil" element={<NedenBizeKatil />} />
        <Route path="/usta-kayit" element={<UstaRegister />} />
        <Route path="/garanti" element={<GarantiPage />} />
        <Route path="/garanti-sartlari" element={<GarantiSartlari />} />
        <Route path="/hakkimizda" element={<HakkimizdaPage />} />
        <Route path="/gizlilik" element={<GizlilikPolitikasi />} />
        <Route path="/kullanim-kosullari" element={<KullanimKosullari />} />
        <Route path="/bilgi-guvenligi" element={<BilgiGuvenligi />} />
        <Route path="/sss" element={<SSS />} />
        
        {/* Ana Hizmet Landing Sayfaları */}
        <Route path="/klima-servisi" element={<KlimaServisi />} />
        <Route path="/beyaz-esya-servisi" element={<BeyazEsyaServisi />} />
        <Route path="/elektrik-servisi" element={<ElektrikServisi />} />
        
        {/* Blog Sayfaları */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Ankara Kombi Servisi SEO Sayfaları */}
        <Route path="/ankara-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-cankaya-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-kecioren-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-yenimahalle-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-mamak-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-etimesgut-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-sincan-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-altindag-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-pursaklar-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-golbasi-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-polatli-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-cubuk-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-beypazari-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-akyurt-kombi-servisi" element={<AnkaraKombiServisi />} />
        <Route path="/ankara-kahramankazan-kombi-servisi" element={<AnkaraKombiServisi />} />

        {/* İstanbul Kombi Servisi SEO Sayfaları */}
        <Route path="/istanbul-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-kadikoy-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-uskudar-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-besiktas-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sisli-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-beyoglu-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-fatih-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-bakirkoy-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-atasehir-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-maltepe-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-kartal-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-pendik-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-umraniye-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sancaktepe-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sultanbeyli-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-tuzla-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-cekmekoy-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-beykoz-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sile-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-avcilar-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-kucukcekmece-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-bahcelievler-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-bagcilar-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-gungoren-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-esenler-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-bayrampasa-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-eyupsultan-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-gaziosmanpasa-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sultangazi-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-kagithane-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-sariyer-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-arnavutkoy-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-basaksehir-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-esenyurt-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-beylikduzu-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-buyukcekmece-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-catalca-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-silivri-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-zeytinburnu-kombi-servisi" element={<IstanbulKombiServisi />} />
        <Route path="/istanbul-adalar-kombi-servisi" element={<IstanbulKombiServisi />} />

        {/* İzmir Kombi Servisi SEO Sayfaları */}
        <Route path="/izmir-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-konak-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-karsiyaka-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-bornova-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-buca-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-cigli-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-bayrakli-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-gaziemir-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-narlidere-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-balcova-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-guzelbahce-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-karabaglar-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-torbali-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-menemen-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-aliaga-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-foca-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-dikili-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-bergama-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-kinik-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-odemis-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-tire-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-bayindir-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-selcuk-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-kemalpasa-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-kiraz-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-beydag-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-seferihisar-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-urla-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-cesme-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-karaburun-kombi-servisi" element={<IzmirKombiServisi />} />
        <Route path="/izmir-menderes-kombi-servisi" element={<IzmirKombiServisi />} />
        
        {/* AI Üretilen Dinamik SEO Sayfaları */}
        <Route path="/:citySlug/:districtSlug/:serviceId" element={<SEOServicePage />} />
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;